import { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GraduationCap, Eye, EyeOff, BookUser, IdCard, KeyRound, Info } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import ThemeToggle from "@/components/ThemeToggle";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import api, { apiErr } from "@/lib/api";
import useSeo from "@/lib/useSeo";

export default function Login() {
  const { login, studentLogin, user, student, userType, loading } = useAuth();
  const navigate = useNavigate();
  useSeo({
    title: "Login — Trishul High School Portal",
    description: "Sign in to the Trishul High School portal. Students, teachers and administrators access dashboards, attendance, results and the AI tutor.",
    path: "/login",
  });

  const [tab, setTab] = useState("teacher"); // "teacher" | "student"

  // Teacher form
  const [phone, setPhone] = useState("");
  const [tPassword, setTPassword] = useState("");

  // Student form
  const [studentId, setStudentId] = useState("");
  const [sPassword, setSPassword] = useState("");

  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [forgotOpen, setForgotOpen] = useState(null);  // null | "student" | "teacher"

  // Show a one-time notice when the user was forced out by a newer login
  // elsewhere (single-session enforcement).
  useEffect(() => {
    if (sessionStorage.getItem("sa_session_ended")) {
      sessionStorage.removeItem("sa_session_ended");
      toast.error("You were signed out because your account was used to sign in on another device.");
    }
  }, []);

  if (!loading && userType === "teacher" && user) {
    const next = user.is_dev_admin
      ? "/devadmin/users"
      : user.role === "utility_admin"
      ? "/utility/settings"
      : (user.role === "admin" || user.role === "super_admin" ? "/admin/dashboard" : "/dashboard");
    return <Navigate to={next} replace />;
  }
  if (!loading && userType === "student" && student) {
    return <Navigate to="/student/dashboard" replace />;
  }

  const submitTeacher = async (e) => {
    e.preventDefault();
    setError("");
    const digits = phone.replace(/\D/g, "").replace(/^91(?=\d{10}$)/, "").replace(/^0(?=\d{10}$)/, "");
    if (!digits) return setError("Please enter your mobile number.");
    if (!/^[6-9]\d{9}$/.test(digits)) return setError("Enter a valid 10-digit Indian mobile number.");
    if (!tPassword) return setError("Please enter your password.");
    setSubmitting(true);
    const res = await login(digits, tPassword);
    setSubmitting(false);
    if (res.ok) {
      if (res.mustChangePassword) {
        toast.message("Set a new password to continue.");
        navigate("/staff/set-password", { replace: true });
        return;
      }
      toast.success("Welcome back!");
      const next = res.user?.is_dev_admin
        ? "/devadmin/users"
        : res.user?.role === "utility_admin"
        ? "/utility/settings"
        : (res.user?.role === "admin" || res.user?.role === "super_admin" ? "/admin/dashboard" : "/dashboard");
      navigate(next, { replace: true });
    } else {
      setError(res.error || "Invalid credentials.");
    }
  };

  const submitStudent = async (e) => {
    e.preventDefault();
    setError("");
    const sid = studentId.trim();
    if (!/^\d{9}$/.test(sid)) return setError("Student ID must be exactly 9 digits (numbers only).");
    if (!sPassword) return setError("Please enter your password (or Student ID on first login).");
    setSubmitting(true);
    const res = await studentLogin(sid, sPassword);
    setSubmitting(false);
    if (res.ok) {
      if (res.mustChangePassword) {
        toast.message("Set a new password to continue.");
        navigate("/student/set-password", { replace: true });
      } else {
        toast.success(`Hi ${res.student?.name?.split(" ")[0] || "there"}!`);
        navigate("/student/dashboard", { replace: true });
      }
    } else {
      setError(res.error || "Invalid Student ID or password.");
    }
  };

  return (
    <div className="relative min-h-screen grid lg:grid-cols-2 bg-slate-50 dark:bg-slate-900 overflow-hidden">
      {/* Page-wide ambient blobs (span both halves so there is no visible seam) */}
      <div aria-hidden className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full bg-sky-300/25 dark:bg-sky-500/15 blur-[120px]" />
      <div aria-hidden className="pointer-events-none absolute -bottom-32 left-1/4 h-80 w-80 rounded-full bg-indigo-300/20 dark:bg-indigo-500/12 blur-[140px]" />
      <div className="hidden lg:flex relative items-center justify-center p-12">
        {/* Top-left brand, matching the landing page */}
        <div className="absolute top-8 left-8 flex items-center gap-3 z-10 animate-in fade-in duration-700" data-testid="login-corner-brand">
          <img src="/trishul-logo.png" alt="Trishul High School" className="h-10 w-10 rounded-full bg-white" />
          <div>
            <div className="font-semibold leading-tight text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>Trishul High School</div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-slate-400 dark:text-white/50">School ERP</div>
          </div>
        </div>
        <div className="relative text-center max-w-xl animate-in fade-in slide-in-from-left-6 duration-700" data-testid="login-brand-panel">
          <div className="inline-flex items-center justify-center mb-8">
            <span className="logo-glow">
              <img src="/trishul-logo.png" alt="Trishul AI Campus" className="h-28 w-28 rounded-full" data-testid="login-brand-logo" />
            </span>
          </div>
          <h1
            className="text-5xl xl:text-6xl 2xl:text-7xl font-bold leading-[1.05] text-slate-900 dark:text-slate-100 tracking-tight"
            style={{ fontFamily: "Outfit" }}
            data-testid="login-brand-title"
          >
            Trishul<br />AI<br /><span className="text-sky-500">Campus</span>
          </h1>
          <p className="mt-6 text-base text-slate-500 dark:text-slate-400 max-w-md mx-auto">
            One home for students, teachers, parents and administrators.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md animate-in fade-in slide-in-from-bottom-6 duration-700 delay-150 fill-mode-both">
          <div className="flex items-center justify-between mb-6">
            <div className="lg:hidden flex items-center gap-3">
              <img src="/trishul-logo.png" alt="Trishul High School" className="h-10 w-10 rounded-full" />
              <span className="font-semibold text-slate-900 dark:text-slate-100" style={{fontFamily:"Outfit"}}>Trishul High School</span>
            </div>
            <div className="ml-auto"><ThemeToggle /></div>
          </div>

          {/* Hero text (above the login panel) */}
          <div className="hidden lg:block mb-8">
            <h2 className="text-3xl font-bold mb-3 text-slate-900 dark:text-slate-100" style={{fontFamily:"Outfit"}}>Every child counted,<br/>every achievement celebrated.</h2>
          </div>

          {/* Tabs */}
          <div className="grid grid-cols-2 gap-2 p-1 rounded-2xl bg-slate-100 dark:bg-slate-800 mb-6" data-testid="login-tabs">
            <button
              type="button"
              onClick={() => { setTab("teacher"); setError(""); }}
              data-testid="login-tab-teacher"
              className={`flex items-center justify-center gap-2 h-11 rounded-xl text-sm font-medium transition-colors ${
                tab === "teacher"
                  ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm"
                  : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-200"
              }`}
            >
              <BookUser className="h-4 w-4" /> Teacher / Admin
            </button>
            <button
              type="button"
              onClick={() => { setTab("student"); setError(""); }}
              data-testid="login-tab-student"
              className={`flex items-center justify-center gap-2 h-11 rounded-xl text-sm font-medium transition-colors ${
                tab === "student"
                  ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm"
                  : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-200"
              }`}
            >
              <IdCard className="h-4 w-4" /> Student
            </button>
          </div>

          {tab === "teacher" ? (
            <>
              <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100" style={{fontFamily:"Outfit"}}>Teacher sign in</h2>
              <p className="text-slate-500 mt-1 mb-6">Welcome back. Let's mark today.</p>
              <form onSubmit={submitTeacher} className="space-y-5" data-testid="login-form-teacher">
                <div className="space-y-2">
                  <Label htmlFor="phone">Mobile number</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm select-none">+91</span>
                    <Input id="phone" type="tel" inputMode="numeric" maxLength={10} value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                      placeholder="10-digit mobile number" data-testid="login-phone-input"
                      className="h-11 rounded-xl pl-12" autoComplete="username"/>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input id="password" type={showPw ? "text" : "password"} value={tPassword} onChange={(e) => setTPassword(e.target.value)}
                      placeholder="••••••••" data-testid="login-password-input" className="h-11 rounded-xl pr-11" autoComplete="current-password"/>
                    <button type="button" onClick={() => setShowPw((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="login-toggle-pw">
                      {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                {error && <div data-testid="login-error" className="text-sm rounded-lg bg-rose-50 text-rose-700 border border-rose-200 px-3 py-2">{error}</div>}
                <Button type="submit" disabled={submitting} data-testid="login-submit-btn"
                  className="w-full h-11 rounded-xl bg-sky-500 hover:bg-sky-600 text-white font-medium">
                  {submitting ? "Signing in…" : "Sign in"}
                </Button>
                <button type="button" onClick={() => setForgotOpen("teacher")} data-testid="forgot-pw-teacher" className="text-xs text-sky-600 hover:underline w-full text-center mt-2">
                  Forgot password?
                </button>
              </form>
            </>
          ) : (
            <>
              <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100" style={{fontFamily:"Outfit"}}>Student sign in</h2>
              <p className="text-slate-500 mt-1 mb-6">Welcome back. Let&apos;s learn today.</p>
              <form onSubmit={submitStudent} className="space-y-5" data-testid="login-form-student">
                <div className="space-y-2">
                  <Label htmlFor="student-id">Student ID</Label>
                  <Input id="student-id" value={studentId}
                    onChange={(e) => setStudentId(e.target.value.replace(/\D/g, "").slice(0, 9))}
                    inputMode="numeric" maxLength={9}
                    placeholder="9-digit Student ID (e.g. 100000123)" data-testid="student-roll-input" className="h-11 rounded-xl" autoComplete="username"/>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="s-pwd">Password</Label>
                  <div className="relative">
                    <Input id="s-pwd" type={showPw ? "text" : "password"} value={sPassword} onChange={(e) => setSPassword(e.target.value)}
                      placeholder="Your Student ID (first login) or password" data-testid="student-password-input" className="h-11 rounded-xl pr-11" autoComplete="current-password"/>
                    <button type="button" onClick={() => setShowPw((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="student-toggle-pw">
                      {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                {error && <div data-testid="login-error" className="text-sm rounded-lg bg-rose-50 text-rose-700 border border-rose-200 px-3 py-2">{error}</div>}
                <Button type="submit" disabled={submitting} data-testid="student-submit-btn"
                  className="w-full h-11 rounded-xl bg-sky-500 hover:bg-sky-600 text-white font-medium">
                  {submitting ? "Signing in…" : "Sign in"}
                </Button>
                <button type="button" onClick={() => setForgotOpen("student")} data-testid="forgot-pw-student" className="text-xs text-sky-600 hover:underline w-full text-center mt-2">
                  Forgot password?
                </button>
              </form>
            </>
          )}
          <div className="mt-10 pt-6 border-t border-slate-100 dark:border-slate-800 text-center text-xs text-slate-400 dark:text-slate-500" data-testid="login-footer">
            © {new Date().getFullYear()} Trishul High School · Built by <a
              href="https://zerolabsdigi.in/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-400 dark:text-slate-500 hover:text-sky-300 cursor-pointer transition-colors"
              data-testid="login-zerolabs-link"
            >Zerolabs Digitals</a>
          </div>
        </div>
      </div>
      <ForgotPasswordDialog kind={forgotOpen} onClose={() => setForgotOpen(null)} />
      <Toaster position="top-right" richColors />
    </div>
  );
}

function ForgotPasswordDialog({ kind, onClose }) {
  const [ident, setIdent] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const open = kind === "student" || kind === "teacher";
  const submit = async () => {
    if (!ident.trim()) { toast.error("Please enter your identifier."); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { kind, identifier: ident.trim() });
      toast.success("Request sent.");
      setIdent("");
      onClose();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not send the request.");
    } finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="forgot-pw-dialog">
        <DialogHeader>
          <DialogTitle style={{fontFamily:"Outfit"}}>
            <span className="inline-flex items-center gap-2"><KeyRound className="h-5 w-5 text-sky-500" /> Forgot password</span>
          </DialogTitle>
          <DialogDescription>Enter your registered mobile number and we'll guide you through resetting your password.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">
            {kind === "student" ? "Student ID or roll number" : "Mobile number"}
          </Label>
          <Input
            value={ident}
            onChange={(e) => setIdent(e.target.value)}
            placeholder={kind === "student" ? "e.g. 100000123 or 5A01" : "e.g. 9876500001"}
            className="h-11 rounded-xl"
            data-testid="forgot-pw-input"
            autoFocus
          />
          <div className="rounded-xl bg-sky-50 dark:bg-sky-950/30 border border-sky-100 dark:border-sky-900 px-3 py-2.5 text-xs text-sky-800 dark:text-sky-200 flex gap-2">
            <Info className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
              {kind === "student"
                ? "This request will go to your class teacher. They will reset your password and share a new one with you."
                : "This request will go to the admin. The admin will reset your password and share a new one with you."}
            </span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="bg-sky-500 hover:bg-sky-600" data-testid="forgot-pw-submit">
            {submitting ? "Sending…" : "Send request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

